import { makeStyles } from '@material-ui/core/styles';
import { useMediaQuery } from 'react-responsive';
import {
  Button, CircularProgress, Typography,
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';
import RefreshIcon from '@material-ui/icons/Refresh';
import { COLORS } from 'src/constants/Color';
import { t } from 'src/lib/language';
import { DonorOrganisationResult, DonorSection } from 'src/models/DonorDashboardModel';

interface Props {
  result: DonorOrganisationResult;
  year: number;
  lang: string;
  formatCurrency: (amount: number) => string;
  downloading: boolean;
  refreshing: boolean;
  disabled: boolean;
  onDownload: () => void;
  onRetry: () => void;
}

const useStyles = makeStyles(() => ({
  card: {
    position: 'relative',
    backgroundColor: COLORS.white,
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: `1px solid ${COLORS.background}`,
    padding: 24,
    '@media (max-width: 680px)': {
      padding: 16,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
    '@media (max-width: 680px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  organisationName: {
    fontFamily: 'Qanelas-Bold',
    fontSize: 18,
    fontWeight: 700,
    color: '#1E293B',
    '@media (max-width: 680px)': {
      fontSize: 16,
    },
  },
  yearBadge: {
    display: 'inline-block',
    marginTop: 4,
    padding: '2px 8px',
    borderRadius: 10,
    backgroundColor: COLORS.primaryLight,
    color: COLORS.primary,
    fontSize: 12,
    fontWeight: 700,
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
    marginTop: 20,
    '@media (max-width: 680px)': {
      gridTemplateColumns: '1fr',
      gap: 12,
    },
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: COLORS.mediumgrey,
  },
  metricValue: {
    fontFamily: 'Qanelas-Bold',
    fontSize: 20,
    fontWeight: 700,
    marginTop: 2,
    '@media (max-width: 680px)': {
      fontSize: 18,
    },
  },
  hint: {
    fontSize: 11,
    color: COLORS.mediumgrey,
    marginTop: 2,
  },
  statusRow: {
    marginTop: 16,
    fontSize: 12,
    color: COLORS.mediumgrey,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  warning: {
    color: COLORS.amber,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.7)',
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const DonorOrganisationCard = (props: Props) => {
  const {
    result, year, lang, formatCurrency, downloading, refreshing, disabled, onDownload, onRetry,
  } = props;
  const classes = useStyles();
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 680px)' });

  const getOrganisationName = () => {
    if (lang === 'fr') {
      return result.nameFr || result.nameEn || result.id;
    }
    return result.nameEn || result.nameFr || result.id;
  };

  const hasFailedSection = (section: DonorSection) => result.failedSections.includes(section);

  const getValue = (section: DonorSection, amount: number) => (
    hasFailedSection(section) ? '—' : formatCurrency(amount)
  );

  const hasActivity = result.metrics.paidTotal > 0
    || result.metrics.scheduledTotal > 0
    || result.metrics.unpaidPledgesTotal > 0;

  const renderStatus = () => {
    if (result.status === 'error' || result.status === 'partial') {
      return (
        <div className={`${classes.statusRow} ${classes.warning}`}>
          <span>
            {result.status === 'error'
              ? t(lang, 'pages.donorDashboard.organisationError')
              : t(lang, 'pages.donorDashboard.partialError')}
          </span>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRetry}>
            {t(lang, 'pages.donorDashboard.retry')}
          </Button>
        </div>
      );
    }
    if (!hasActivity) {
      return (
        <div className={classes.statusRow}>
          {t(lang, 'pages.donorDashboard.noActivity', { year })}
        </div>
      );
    }
    return null;
  };

  return (
    <div className={classes.card}>
      <div className={classes.header}>
        <div>
          <Typography className={classes.organisationName}>
            {getOrganisationName()}
          </Typography>
          <span className={classes.yearBadge}>{year}</span>
        </div>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={downloading ? <CircularProgress size={16} /> : <GetAppIcon />}
          disabled={disabled || downloading}
          fullWidth={isTabletOrMobile}
          onClick={onDownload}>
          {t(lang, 'pages.donorDashboard.downloadReceipts')}
        </Button>
      </div>

      <div className={classes.metrics}>
        <div>
          <div className={classes.metricLabel}>
            {t(lang, 'pages.donorDashboard.paidTotal')}
          </div>
          <Typography
            className={classes.metricValue}
            style={{ color: COLORS.action }}>
            {getValue('paid', result.metrics.paidTotal)}
          </Typography>
        </div>

        <div>
          <div className={classes.metricLabel}>
            {t(lang, 'pages.donorDashboard.taxDeductible')}
          </div>
          <Typography
            className={classes.metricValue}
            style={{ color: COLORS.secondary }}>
            {getValue('paid', result.metrics.deductibleTotal)}
          </Typography>
        </div>

        <div>
          <div className={classes.metricLabel}>
            {t(lang, 'pages.donorDashboard.scheduledTotal')}
          </div>
          <Typography
            className={classes.metricValue}
            style={{ color: COLORS.primary }}>
            {getValue('scheduled', result.metrics.scheduledTotal)}
          </Typography>
        </div>

        <div>
          <div className={classes.metricLabel}>
            {t(lang, 'pages.donorDashboard.unpaidPledges')}
          </div>
          <Typography
            className={classes.metricValue}
            style={{ color: COLORS.amber }}>
            {getValue('unpaid', result.metrics.unpaidPledgesTotal)}
          </Typography>
          {!hasFailedSection('unpaid') && (
            <div className={classes.hint}>
              {t(lang, 'pages.donorDashboard.pledgesCount', {
                count: result.metrics.unpaidPledgesCount,
              })}
            </div>
          )}
        </div>
      </div>

      {renderStatus()}

      {refreshing && (
        <div className={classes.overlay}>
          <CircularProgress size={24} />
        </div>
      )}
    </div>
  );
};

export default DonorOrganisationCard;
